import { useEffect, useState } from 'react';
import {
    createPickupLocationAdmin,
    deletePickupLocationAdmin,
    getPickupLocationsAdmin,
    updatePickupLocationAdmin,
    type PickupLocationAdminDto,
} from '../../services/api.admin';

const empty = { city: '', region: '', isActive: true, sortOrder: 0 };

const PickupLocationsSection = () => {
    const [rows, setRows] = useState<PickupLocationAdminDto[]>([]);
    const [form, setForm] = useState(empty);
    const [editingId, setEditingId] = useState<number | null>(null);
    const [error, setError] = useState<string | null>(null);

    const load = async () => {
        setRows(await getPickupLocationsAdmin());
    };

    useEffect(() => {
        void load();
    }, []);

    const save = async () => {
        setError(null);
        const body = {
            city: form.city.trim(),
            region: form.region.trim() || null,
            isActive: form.isActive,
            sortOrder: Number(form.sortOrder) || 0,
        };
        try {
            if (editingId) await updatePickupLocationAdmin(editingId, body);
            else await createPickupLocationAdmin(body);
            setForm(empty);
            setEditingId(null);
            await load();
        } catch (e: unknown) {
            const msg =
                e && typeof e === 'object' && 'response' in e
                    ? String((e as { response?: { data?: { message?: string } } }).response?.data?.message)
                    : 'Could not save';
            setError(msg || 'Could not save');
        }
    };

    return (
        <div className="space-y-6">
            <div>
                <h2 className="text-xl font-semibold">Pickup cities</h2>
                <p className="mt-1 text-sm text-gray-600">
                    Public listings show the city only. No shop names, parking, or street. Checkout pickup is a later ticket.
                </p>
            </div>
            {error && <p className="text-sm text-red-600">{error}</p>}
            <div className="flex flex-wrap items-end gap-3 rounded-xl border p-4">
                <label className="text-sm">
                    City
                    <input
                        className="mt-1 block rounded-lg border px-3 py-2"
                        value={form.city}
                        onChange={(e) => setForm((f) => ({ ...f, city: e.target.value }))}
                    />
                </label>
                <label className="text-sm">
                    Region
                    <input
                        className="mt-1 block rounded-lg border px-3 py-2"
                        value={form.region}
                        onChange={(e) => setForm((f) => ({ ...f, region: e.target.value }))}
                    />
                </label>
                <label className="text-sm">
                    Sort
                    <input
                        type="number"
                        className="mt-1 block w-20 rounded-lg border px-3 py-2"
                        value={form.sortOrder}
                        onChange={(e) => setForm((f) => ({ ...f, sortOrder: Number(e.target.value) }))}
                    />
                </label>
                <label className="flex items-center gap-2 text-sm">
                    <input
                        type="checkbox"
                        checked={form.isActive}
                        onChange={(e) => setForm((f) => ({ ...f, isActive: e.target.checked }))}
                    />
                    Active
                </label>
                <button
                    type="button"
                    onClick={() => void save()}
                    className="rounded-lg bg-[var(--accent-color,#334D3A)] px-4 py-2 text-sm font-semibold text-white"
                >
                    {editingId ? 'Update' : 'Add city'}
                </button>
            </div>
            <table className="w-full text-left text-sm">
                <thead>
                    <tr className="border-b">
                        <th className="py-2">City</th>
                        <th>Region</th>
                        <th>Active</th>
                        <th />
                    </tr>
                </thead>
                <tbody>
                    {rows.map((r) => (
                        <tr key={r.id} className="border-b">
                            <td className="py-2">{r.city}</td>
                            <td>{r.region}</td>
                            <td>{r.isActive ? 'Yes' : 'No'}</td>
                            <td className="space-x-2">
                                <button
                                    type="button"
                                    className="underline"
                                    onClick={() => {
                                        setEditingId(r.id);
                                        setForm({
                                            city: r.city,
                                            region: r.region ?? '',
                                            isActive: r.isActive,
                                            sortOrder: r.sortOrder,
                                        });
                                    }}
                                >
                                    Edit
                                </button>
                                <button
                                    type="button"
                                    className="underline"
                                    onClick={async () => {
                                        try {
                                            await deletePickupLocationAdmin(r.id);
                                            await load();
                                        } catch (e: unknown) {
                                            const msg =
                                                e && typeof e === 'object' && 'response' in e
                                                    ? String(
                                                          (e as { response?: { data?: { message?: string } } })
                                                              .response?.data?.message
                                                      )
                                                    : 'Cannot delete';
                                            setError(msg || 'Cannot delete');
                                        }
                                    }}
                                >
                                    Delete
                                </button>
                            </td>
                        </tr>
                    ))}
                </tbody>
            </table>
        </div>
    );
};

export default PickupLocationsSection;
